import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { DialogService } from '../../../shared/services/dialog.service';
import { IDetailsControl } from '../../../shared/models/details-control';
import { BookAccessDetailsService } from '../../../../api/api-services/book-access-details.service';
import { IBookAccessHistoryMetaVm } from '../../../../api/book-access-history-meta-vm.model';
import { IBookAccessHistoryVm } from '../../../../api/book-access-history-vm.model';
import { IAccessTicketVm } from '../../../../api/access-ticket-vm.model';
import { centerDynamicModalConfig } from '../../../shared/helpers/modal.config';
import { eModalRoles } from '../../../shared/models/modals';
import { DialogService as PrimeNgDialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { QrcodeDetailsComponent } from '../qrcode-details/qrcode-details.component';
import { InstructionPdfService } from '../../../shared/services/instruction-pdf.service';
import {
  InstructionOptionsComponent,
  InstructionOptionsResult,
} from '../instruction-options/instruction-options.component';

// Label sheet layout: 52.5 x 29.7mm labels, 4 columns x 10 rows = 40 labels per A4 sheet.
const LABELS_PER_ROW = 4;
const LABELS_PER_PAGE = 40;

@Component({
  selector: 'app-book-access-package-details',
  templateUrl: './book-access-package-details.component.html',
  styleUrl: './book-access-package-details.component.scss',
})
export class BookAccessPackageDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() ID_dialog!: string;
  @Input() dataIndex!: number;
  @Input() dataList!: IBookAccessHistoryMetaVm[];
  @Input() data!: IBookAccessHistoryMetaVm;
  @Output() changed = new EventEmitter<void>();

  private ref: DynamicDialogRef | undefined;
  private _primengDialogService = inject(PrimeNgDialogService)
  private _bookAccessDetailsService = inject(BookAccessDetailsService);
  private _dialogService = inject(DialogService);
  private _messageService = inject(MessageService);
  private _instructionPdfService = inject(InstructionPdfService);

  private dialogStateSubscription!: Subscription;

  public dialogState!: IDetailsControl;
  public packageDetails!: IBookAccessHistoryVm;
  public readonly labelsPerRow = LABELS_PER_ROW;
  public generatingInstructionsAll = false;
  public generatingInstructionCode: string | null = null;

  // Chunks tickets into full 40-slot label pages (matching the physical A4 label sheet layout).
  // Even a single ticket produces one full page, filled from the top-left slot, remaining slots left blank
  // so the template stays aligned with a partially used physical sheet.
  // Computed once whenever packageDetails is (re)loaded, NOT as a template getter: the <qrcode> element
  // renders asynchronously (its ngOnChanges returns a Promise), which re-enters Angular's zone and
  // schedules another change-detection pass. A getter would rebuild a brand-new array of arrays on that
  // pass too, so *ngFor would see "changed" input forever, re-rendering all label QR codes in an
  // infinite loop that freezes the tab. Caching the result here breaks that loop.
  public labelPages: (IAccessTicketVm | null)[][] = [];

  private buildLabelPages(): void {
    const tickets = this.packageDetails?.accessTickets ?? [];
    const pageCount = Math.max(1, Math.ceil(tickets.length / LABELS_PER_PAGE));
    const pages: (IAccessTicketVm | null)[][] = [];
    for (let p = 0; p < pageCount; p++) {
      const page: (IAccessTicketVm | null)[] = [];
      for (let i = 0; i < LABELS_PER_PAGE; i++) {
        page.push(tickets[p * LABELS_PER_PAGE + i] ?? null);
      }
      pages.push(page);
    }
    this.labelPages = pages;
  }

  ngOnInit(): void {
    this.dialogStateSubscription = this._dialogService
      .getDialogState(this.ID_dialog)
      .subscribe((state) => {
        this.dialogState = state;
      });

    this.loadPackageDetails();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.data && changes['data']) {
      this.loadPackageDetails();
    }
  }

  ngOnDestroy(): void {
    this.dialogStateSubscription.unsubscribe();
  }

  private loadPackageDetails(): void {
    if (!this.data || !this.data.guid) {
      return;
    }
    this._bookAccessDetailsService.get(this.data.guid).subscribe({
      next: (res: IBookAccessHistoryVm) => {
        this.packageDetails = res;
        this.buildLabelPages();
      },
      error: (err) => {
        console.error('Błąd pobierania listy dostępu:', err);
      },
    });
  }

  buildSignUpUrl(code: string): string {
    return `https://zdamyprawko.pl/app/sign-up?code=${code}`;
  }

  closeDialog(): void {
    this._dialogService.closeDialog(this.ID_dialog);
  }

  moveUp(): void {
    this.dataIndex = (this.dataIndex + 1) % this.dataList.length;
    this.data = this.dataList[this.dataIndex];
    this.loadPackageDetails();
  }

  moveDown(): void {
    this.dataIndex = (this.dataIndex - 1 + this.dataList.length) % this.dataList.length;
    this.data = this.dataList[this.dataIndex];
    this.loadPackageDetails();
  }

  printList(): void {
    this.withRelocatedLabelSheet(() => window.print());
    this._bookAccessDetailsService
      .put({
        guid: this.packageDetails.guid,
        timeLimit: this.packageDetails.timeLimit,
        validDays: this.packageDetails.validDays,
        printed: true,
      })
      .subscribe({
        next: (res) => {
          this.packageDetails = res;
          this.buildLabelPages();
          this.changed.emit();
        },
      });
  }

  // Temporarily lifts the label sheet out of the PrimeNG dialog (which appendTo="body" wraps its
  // content in fixed-size, clipping/scrolling containers) to be a direct child of <body> for the
  // duration of window.print(). As a top-level body child in normal flow it is neither clipped by
  // the dialog nor pinned by position:fixed, so the label pages paginate correctly across multiple
  // A4 sheets (packages with more than 40 tickets). window.print() blocks synchronously, so the
  // node is restored to its exact original position before Angular change detection runs again.
  private withRelocatedLabelSheet(print: () => void): void {
    const sheet = document.querySelector('.label-print-only') as HTMLElement | null;
    const anchor = sheet ? document.createComment('label-print-anchor') : null;
    if (sheet && anchor && sheet.parentNode) {
      sheet.parentNode.insertBefore(anchor, sheet);
      document.body.appendChild(sheet);
    }
    // Browsers use document.title as the default "Save as PDF" filename, so name the print
    // "PrawkoNaklejki_dd-mm" and restore the original title once printing finishes.
    const originalTitle = document.title;
    document.title = this.buildLabelFileName();
    try {
      print();
    } finally {
      document.title = originalTitle;
      if (sheet && anchor && anchor.parentNode) {
        anchor.parentNode.replaceChild(sheet, anchor);
      }
    }
  }

  // Print/download filename base: "PrawkoNaklejki_dd-mm" (day and month, zero-padded).
  private buildLabelFileName(): string {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    return `PrawkoNaklejki_${dd}-${mm}`;
  }

  // Deletion is blocked once the package has been marked as printed or any of its codes has been
  // redeemed — enforced authoritatively on the backend, mirrored here to disable the UI control.
  get isDeleteLocked(): boolean {
    if (!this.packageDetails) {
      return false;
    }
    return this.packageDetails.printed || (this.packageDetails.accessTickets ?? []).some((t) => t.isUsed);
  }

  downloadInstructionForTicket(ticket: IAccessTicketVm, rowIndex: number): void {
    if (this.generatingInstructionCode) {
      return;
    }
    const optionsRef = this._primengDialogService.open(InstructionOptionsComponent, {
      ...centerDynamicModalConfig,
      width: '450px',
      breakpoints: { '450px': '100vw' },
      data: { mode: 'single' },
    });

    optionsRef.onClose.subscribe(async (result: InstructionOptionsResult | undefined) => {
      if (!result) {
        return;
      }
      this.generatingInstructionCode = ticket.code;
      try {
        await this._instructionPdfService.downloadOne(ticket, rowIndex + 1, result.language);
      } catch (err) {
        console.error('Błąd generowania instrukcji:', err);
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się wygenerować instrukcji. Spróbuj ponownie później.',
        });
      } finally {
        this.generatingInstructionCode = null;
      }
    });
  }

  downloadAllInstructions(): void {
    const tickets = this.packageDetails?.accessTickets ?? [];
    if (!tickets.length || this.generatingInstructionsAll) {
      return;
    }
    const optionsRef = this._primengDialogService.open(InstructionOptionsComponent, {
      ...centerDynamicModalConfig,
      width: '450px',
      breakpoints: { '450px': '100vw' },
      data: { mode: 'bulk' },
    });

    optionsRef.onClose.subscribe(async (result: InstructionOptionsResult | undefined) => {
      if (!result) {
        return;
      }
      this.generatingInstructionsAll = true;
      try {
        await this._instructionPdfService.downloadBulk(tickets, result.language, result.format);
            this._bookAccessDetailsService
      .put({
        guid: this.packageDetails.guid,
        timeLimit: this.packageDetails.timeLimit,
        validDays: this.packageDetails.validDays,
        printed: true,
      })
      .subscribe({
        next: (res) => {
           this.changed.emit();
        },
      });
      } catch (err) {
        console.error('Błąd generowania paczki instrukcji:', err);
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się wygenerować paczki instrukcji. Spróbuj ponownie później.',
        });
      } finally {
        this.generatingInstructionsAll = false;
      }
    });
  }

  openQrCode(data: IAccessTicketVm): void {
    console.log(data)
        this.ref = this._primengDialogService.open(QrcodeDetailsComponent, {
          ...centerDynamicModalConfig,
          data,
          header: 'Podgląd kodu QR',
          showHeader: true,
          closable: true,
        });
    
        this.ref.onClose.subscribe((res: eModalRoles | undefined) => {
        });
  }

  deleteList(): void {
    if (this.isDeleteLocked) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Nie można usunąć',
        detail: this.packageDetails.printed
          ? 'Lista została oznaczona jako wydrukowana i nie może zostać usunięta.'
          : 'Co najmniej jeden kod z listy został wykorzystany — lista nie może zostać usunięta.',
      });
      return;
    }
    this._bookAccessDetailsService.delete(this.data.guid).subscribe({
      next: () => {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Lista została usunięta.',
        });
        this.changed.emit();
        this.closeDialog();
      },
      error: (err) => {
        console.error('Błąd usuwania listy dostępu:', err);
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Wystąpił błąd. Spróbuj ponownie później.',
        });
      },
    });
  }
}
